import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEMO_PESTS } from '../../data/demo';

type Risk = 'high' | 'medium' | 'low';
type Trend = 'up' | 'stable' | 'down';

const RISK_COLOR: Record<Risk, string> = {
  high:   'var(--red)',
  medium: 'var(--amber)',
  low:    'var(--green)',
};
const RISK_BG: Record<Risk, string> = {
  high:   'var(--red-bg)',
  medium: 'var(--amber-bg)',
  low:    'var(--green-bg)',
};
const TREND_ICON: Record<Trend, string> = {
  up: '↑', stable: '→', down: '↓',
};
const TREND_COLOR: Record<Trend, string> = {
  up: 'var(--red)', stable: 'var(--amber)', down: 'var(--green)',
};

export default function PestPage() {
  const { t } = useTranslation();
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const { zones, recentAlerts, history } = DEMO_PESTS;

  const selected = zones.find(z => z.id === selectedZone);
  const alertZones = zones.filter(z => z.risk === 'high').length;

  return (
    <div className="page">
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{t('pest.title')}</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>
            {t('pest.subtitle', { count: alertZones })}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
        {/* Left: Zones + history */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Zone cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            {zones.map(zone => (
              <div
                key={zone.id}
                onClick={() => setSelectedZone(selectedZone === zone.id ? null : zone.id)}
                style={{
                  background: 'var(--white)', borderRadius: 12, padding: '16px', cursor: 'pointer', transition: 'all 0.15s',
                  border: selectedZone === zone.id
                    ? `1.5px solid ${RISK_COLOR[zone.risk]}`
                    : '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t(`pest.zones.${zone.id}`)}</div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: RISK_BG[zone.risk], color: RISK_COLOR[zone.risk],
                  }}>
                    {t(`pest.risk.${zone.risk}`)}
                  </span>
                </div>

                <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 8 }}>
                  {zone.pests.length > 0
                    ? zone.pests.map(p => t(`pest.pests.${p}`, p)).join(', ')
                    : t('pest.noDetections')}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {t(`pest.inspectionTimes.${zone.lastInspection}`)}
                  </div>
                  <span style={{ fontSize: 14, color: TREND_COLOR[zone.trend], fontWeight: 700 }}>
                    {TREND_ICON[zone.trend]}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Zone detail */}
          {selected && (
            <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                {t('pest.detail')} — {t(`pest.zones.${selected.id}`)}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <InfoBox label={t('pest.riskLabel')}         value={t(`pest.risk.${selected.risk}`)}      color={RISK_COLOR[selected.risk]} />
                <InfoBox label={t('pest.trendLabel')}        value={`${TREND_ICON[selected.trend]} ${t(`pest.trend.${selected.trend}`)}`} color={TREND_COLOR[selected.trend]} />
                <InfoBox label={t('pest.lastInspection')}    value={t(`pest.inspectionTimes.${selected.lastInspection}`)} />
              </div>
              {selected.pests.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 6 }}>{t('pest.detectedPests')}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selected.pests.map(p => (
                      <span key={p} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 99, background: 'var(--red-bg)', color: 'var(--red)', fontWeight: 500 }}>
                        {t(`pest.pests.${p}`, p)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Weekly history */}
          <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{t('pest.weeklyHistory')}</h3>
            <MiniBarChart data={history} />
          </div>
        </div>

        {/* Right: Recent alerts */}
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{t('pest.recentAlerts')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {recentAlerts.map(alert => (
              <div key={alert.id} style={{
                background: 'var(--white)', borderRadius: 10, border: '1px solid var(--border)',
                padding: '12px 14px', borderLeft: `3px solid ${RISK_COLOR[alert.severity]}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{t(`pest.pests.${alert.pest}`, alert.pest)}</div>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99,
                    background: RISK_BG[alert.severity], color: RISK_COLOR[alert.severity],
                  }}>
                    {t(`pest.risk.${alert.severity}`)}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 6 }}>
                  {t(`pest.zones.${alert.zoneId}`)} · {t(`pest.alertTimes.${alert.timeKey}`)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--dark)', background: 'var(--bg)', borderRadius: 6, padding: '6px 8px' }}>
                  {t(`pest.actions.${alert.id}`)}
                </div>
              </div>
            ))}
          </div>

          {/* Global risk summary */}
          <div style={{ marginTop: 20, background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{t('pest.globalRisk')}</h3>
            {(['high', 'medium', 'low'] as Risk[]).map(r => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 60, fontSize: 11, color: 'var(--gray)' }}>{t(`pest.risk.${r}`)}</div>
                <div style={{ flex: 1, height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${(zones.filter(z => z.risk === r).length / zones.length) * 100}%`, height: '100%', background: RISK_COLOR[r], borderRadius: 3 }} />
                </div>
                <div style={{ width: 16, fontSize: 11, fontWeight: 600, color: RISK_COLOR[r], textAlign: 'right' }}>
                  {zones.filter(z => z.risk === r).length}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, color: 'var(--gray)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: color ?? 'var(--dark)' }}>{value}</div>
    </div>
  );
}

function MiniBarChart({ data }: { data: typeof DEMO_PESTS['history'] }) {
  const { t } = useTranslation();
  const seriesKeys: Array<{ key: keyof typeof data; labelKey: string; color: string }> = [
    { key: 'Whitefly',    labelKey: 'pest.series.Whitefly',    color: 'var(--red)' },
    { key: 'Spider Mite', labelKey: 'pest.series.Spider Mite', color: 'var(--amber)' },
    { key: 'Aphid',       labelKey: 'pest.series.Aphid',       color: '#8b5cf6' },
    { key: 'Thrips',      labelKey: 'pest.series.Thrips',      color: 'var(--green)' },
  ];

  const allValues = seriesKeys.flatMap(s => data[s.key] as number[]);
  const maxVal = Math.max(...allValues, 1);

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80, marginBottom: 8 }}>
        {data.labels.map((day, di) => (
          <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
            {seriesKeys.map(s => {
              const val = (data[s.key] as number[])[di];
              return (
                <div key={s.key} style={{
                  width: '100%', height: `${(val / maxVal) * 60}px`,
                  background: s.color, borderRadius: 2, opacity: 0.8,
                  minHeight: val > 0 ? 3 : 0,
                }} />
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        {data.labels.map(day => (
          <div key={day} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'var(--gray)' }}>
            {t(`pest.days.${day}`)}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {seriesKeys.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
            <span style={{ fontSize: 10, color: 'var(--gray)' }}>{t(s.labelKey)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
